import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import PageErrorBoundary from '@/components/PageErrorBoundary';
import {
  ArrowLeft, Download, Loader2, Save, Trash2, Send, FileText,
  CheckCircle2, AlertTriangle, Building2, Calendar, Hash, Plus, X,
} from 'lucide-react';
import { parseFR } from '@/lib/utils';
import NumInputFR from '@/components/NumInputFR';

const STATUT_STYLE = {
  'À vérifier':      'bg-amber-100 text-amber-700',
  'Validée':         'bg-blue-100 text-blue-700',
  'Poussée Pennylane': 'bg-emerald-100 text-emerald-700',
};

export default function FactureAchatDetail() {
  return (
    <PageErrorBoundary>
      <FactureAchatDetailInner />
    </PageErrorBoundary>
  );
}

function FactureAchatDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fa, setFa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  // Incremented after every successful save/push to force a clean DOM remount.
  // Works around an Emergent visual-editor quirk that injects <span> nodes
  // inside conditional renders and crashes React on subsequent re-renders.
  const [remountKey, setRemountKey] = useState(0);
  const bumpRemount = () => setRemountKey((k) => k + 1);

  const load = async () => {
    try {
      const r = await api.get(`/factures-achat/${id}`);
      setFa(r.data);
      setForm({
        fournisseur: r.data.fournisseur || {},
        numero_facture: r.data.numero_facture || '',
        date_facture: r.data.date_facture || '',
        date_echeance: r.data.date_echeance || '',
        description: r.data.description || '',
        montants: r.data.montants || { total_ht: 0, total_tva: 0, total_ttc: 0, tva_taux: 20 },
        lignes: r.data.lignes || [],
        statut: r.data.statut || 'À vérifier',
        notes: r.data.notes || '',
        chantier_id: r.data.chantier_id || '',
      });
    } catch (e) {
      toast.error("Facture d'achat introuvable");
      navigate('/factures-achat');
    }
    setLoading(false);
  };

  const loadChantiers = async () => {
    try {
      const r = await api.get('/chantiers');
      setChantiers(r.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); loadChantiers(); }, [id]);

  const linkChantier = async (chantierId) => {
    try {
      await api.patch(`/factures-achat/${id}/chantier`, { chantier_id: chantierId || null });
      setForm((p) => ({ ...p, chantier_id: chantierId || '' }));
      setFa((p) => (p ? { ...p, chantier_id: chantierId || null } : p));
      toast.success(chantierId ? 'Chantier lié' : 'Chantier délié');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/factures-achat/${id}`, form);
      if (r?.data) setFa(r.data);
      toast.success('Modifications enregistrées');
      bumpRemount();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur lors de la sauvegarde';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      console.error('[FactureAchat] save error', e);
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/factures-achat/${id}`, { ...form, statut: 'Validée' });
      if (r?.data) {
        setFa(r.data);
        setForm((p) => ({ ...p, statut: 'Validée' }));
      }
      toast.success('Facture validée — prête pour Pennylane');
      bumpRemount();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      console.error('[FactureAchat] validate error', e);
    } finally {
      setSaving(false);
    }
  };

  const pushPennylane = async () => {
    if (!window.confirm("Envoyer définitivement cette facture sur Pennylane ?")) return;
    setPushing(true);
    try {
      // On utilise l'endpoint bulk asynchrone (background task) au lieu du
      // push sync direct : le sync peut dépasser le timeout Cloudflare de
      // 100 s (upload PDF + création fournisseur + import), ce qui provoque
      // un 502 "invalid or incomplete response". L'async retourne immédiate-
      // ment et on suit la progression via l'endpoint /progress.
      await api.post('/factures-achat/bulk/push-pennylane', { ids: [id] });
      toast.success('Envoi Pennylane lancé…', { duration: 4000 });

      // Poll pour connaître le résultat final
      let tries = 0;
      const finish = () => {
        setPushing(false);
        bumpRemount();
        load().catch((err) => console.error('[FactureAchat] reload after push failed', err));
      };
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const r = await api.get('/factures-achat/bulk/push-pennylane/progress');
          const p = r.data;
          if (!p?.running) {
            clearInterval(iv);
            if ((p?.pushed || 0) > 0 && (p?.failed || 0) === 0) {
              toast.success('Facture poussée sur Pennylane ✓', { duration: 6000 });
            } else if ((p?.failed || 0) > 0) {
              const errDetail = p.errors?.[0]?.error || 'Erreur inconnue';
              toast.error(`Échec Pennylane : ${errDetail}`, { duration: 12000 });
            }
            finish();
          }
        } catch (_e) { /* ignore poll errors */ }
        if (tries > 90) { // 3 min max
          clearInterval(iv);
          toast.warning("Traitement Pennylane en cours — rechargez la page dans quelques instants", { duration: 8000 });
          finish();
        }
      }, 2000);
    } catch (e) {
      setPushing(false);
      const detail = e?.response?.data?.detail || e?.message || 'Échec push Pennylane';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail), { duration: 10000 });
      console.error('[FactureAchat] push error', e);
    }
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement cette facture ?')) return;
    setDeleting(true);
    try {
      await api.delete(`/factures-achat/${id}`);
      toast.success('Facture supprimée');
      navigate('/factures-achat');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
      setDeleting(false);
    }
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/factures-achat/${id}/pdf`;
    window.open(url, '_blank');
  };

  const setMnt = (k, v) => setForm((p) => ({ ...p, montants: { ...p.montants, [k]: parseFloat(v) || 0 } }));
  const setFou = (k, v) => setForm((p) => ({ ...p, fournisseur: { ...p.fournisseur, [k]: v } }));

  if (loading || !fa || !form) {
    return <div key="fa-detail-loading" className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-indigo-600" /></div>;
  }

  return (
    <div key={`fa-detail-${remountKey}`} className="space-y-5" data-testid="fa-detail-page">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/factures-achat')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div>
              <div
                key={`badges-${fa.pennylane_invoice_id || 'none'}`}
                className="flex items-center gap-2 flex-wrap"
              >
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {form.fournisseur?.nom || fa.filename || 'Facture d\'achat'}
                </h1>
                <Badge className={STATUT_STYLE[form.statut] || 'bg-slate-100 text-slate-700'}>{form.statut}</Badge>
                {fa.pennylane_invoice_id && (
                  <Badge key="badge-pennylane" className="bg-emerald-100 text-emerald-700">Pennylane #{fa.pennylane_invoice_id}</Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Importée le {fa.created_at?.slice(0, 10)} par {fa.uploaded_by}
              </p>
            </div>
          </div>
          <div
            key={`actions-${form.statut}-${fa.pennylane_invoice_id ? 'pushed' : 'pending'}`}
            className="flex items-center gap-2"
          >
            <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="fa-pdf-btn">
              <Download className="w-4 h-4 mr-1" /> PDF original
            </Button>
            <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="fa-save-btn">
              {saving
                ? <Loader2 key="ic-saving" className="w-4 h-4 mr-1 animate-spin" />
                : <Save key="ic-save" className="w-4 h-4 mr-1" />}
              Enregistrer
            </Button>
            {form.statut === 'À vérifier' && (
              <Button key="btn-validate" size="sm" onClick={validate} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="fa-validate-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Valider
              </Button>
            )}
            {form.statut === 'Validée' && !fa.pennylane_invoice_id && (
              <Button key="btn-push" size="sm" onClick={pushPennylane} disabled={pushing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="fa-push-btn">
                {pushing
                  ? <Loader2 key="ic-pushing" className="w-4 h-4 mr-1 animate-spin" />
                  : <Send key="ic-push" className="w-4 h-4 mr-1" />}
                Envoyer sur Pennylane
              </Button>
            )}
            {!fa.pennylane_invoice_id && (
              <Button key="btn-delete" variant="ghost" size="sm" onClick={remove} disabled={deleting} className="text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* OCR warning */}
      {form.statut === 'À vérifier' && (
        <div key="ocr-warning" className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            <b>OCR automatique</b> — vérifiez attentivement le fournisseur, les montants et les dates avant de valider.
            L'IA peut faire des erreurs (chiffres mal lus, taux de TVA incorrect, etc.).
          </p>
        </div>
      )}

      {/* Fournisseur + dates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Fournisseur
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Raison sociale"><Input value={form.fournisseur?.nom || ''} onChange={(e) => setFou('nom', e.target.value)} data-testid="fa-fou-nom" /></Field>
            <Field label="SIRET (14 chiffres)"><Input value={form.fournisseur?.siret || ''} onChange={(e) => setFou('siret', e.target.value)} placeholder="—" /></Field>
            <Field label="Adresse"><Input value={form.fournisseur?.adresse || ''} onChange={(e) => setFou('adresse', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="CP"><Input value={form.fournisseur?.code_postal || ''} onChange={(e) => setFou('code_postal', e.target.value)} /></Field>
              <Field label="Ville"><Input value={form.fournisseur?.ville || ''} onChange={(e) => setFou('ville', e.target.value)} /></Field>
            </div>
            <Field label="Email"><Input value={form.fournisseur?.email || ''} onChange={(e) => setFou('email', e.target.value)} placeholder="—" /></Field>
            <Field label="Téléphone"><Input value={form.fournisseur?.telephone || ''} onChange={(e) => setFou('telephone', e.target.value)} placeholder="—" /></Field>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Hash className="w-4 h-4" /> Identifiants
          </h3>
          <div className="space-y-3">
            <Field label="N° facture"><Input value={form.numero_facture} onChange={(e) => setForm((p) => ({ ...p, numero_facture: e.target.value }))} data-testid="fa-num-input" /></Field>
            <Field label="Date facture"><Input type="date" value={form.date_facture || ''} onChange={(e) => setForm((p) => ({ ...p, date_facture: e.target.value }))} data-testid="fa-date-input" /></Field>
            <Field label="Date échéance"><Input type="date" value={form.date_echeance || ''} onChange={(e) => setForm((p) => ({ ...p, date_echeance: e.target.value }))} /></Field>
          </div>
        </div>
      </div>

      {/* Montants */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Montants</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Total HT (€)"><Input type="number" step="0.01" value={form.montants?.total_ht ?? 0} onChange={(e) => setMnt('total_ht', e.target.value)} data-testid="fa-total-ht" /></Field>
          <Field label="TVA (€)"><Input type="number" step="0.01" value={form.montants?.total_tva ?? 0} onChange={(e) => setMnt('total_tva', e.target.value)} /></Field>
          <Field label="Taux TVA (%)"><Input type="number" step="0.5" value={form.montants?.tva_taux ?? 20} onChange={(e) => setMnt('tva_taux', e.target.value)} /></Field>
          <Field label="Total TTC (€)"><Input type="number" step="0.01" value={form.montants?.total_ttc ?? 0} onChange={(e) => setMnt('total_ttc', e.target.value)} data-testid="fa-total-ttc" /></Field>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Description</h3>
        <Textarea
          value={form.description || ''}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          rows={2}
          placeholder="Court résumé du contenu de la facture"
        />
      </div>

      {/* Lignes */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Lignes ({(form.lignes || []).length})</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setForm((p) => ({ ...p, lignes: [...(p.lignes || []), { designation: '', quantite: 1, prix_unitaire_ht: 0, total_ht: 0 }] }))}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </div>
        {(form.lignes || []).length === 0 ? (
          <p key="no-lines" className="text-sm text-slate-400 italic text-center py-4">Aucune ligne détectée par l'OCR — facultatif</p>
        ) : (
          <div key="lines-list" className="space-y-2">
            {form.lignes.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Input
                  className="col-span-6"
                  value={li.designation || ''}
                  onChange={(e) => setForm((p) => ({ ...p, lignes: p.lignes.map((x, j) => j === i ? { ...x, designation: e.target.value } : x) }))}
                  placeholder="Désignation"
                />
                <NumInputFR className="col-span-1" value={li.quantite ?? 1} onChange={(n) => setForm((p) => ({ ...p, lignes: p.lignes.map((x, j) => j === i ? { ...x, quantite: n } : x) }))} />
                <NumInputFR className="col-span-2" value={li.prix_unitaire_ht ?? 0} onChange={(n) => setForm((p) => ({ ...p, lignes: p.lignes.map((x, j) => j === i ? { ...x, prix_unitaire_ht: n } : x) }))} placeholder="PU HT" />
                <NumInputFR className="col-span-2" value={li.total_ht ?? 0} onChange={(n) => setForm((p) => ({ ...p, lignes: p.lignes.map((x, j) => j === i ? { ...x, total_ht: n } : x) }))} placeholder="Total HT" />
                <Button size="icon" variant="ghost" onClick={() => setForm((p) => ({ ...p, lignes: p.lignes.filter((_, j) => j !== i) }))} className="text-red-600">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chantier lié */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">
          Chantier lié
          <span className="ml-2 text-xs font-normal text-slate-500">(imputation dépense pour marges)</span>
        </h3>
        <select
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={form.chantier_id || ''}
          onChange={(e) => linkChantier(e.target.value)}
          data-testid="fa-chantier-select"
        >
          <option value="">— Aucun chantier —</option>
          {chantiers.map((c) => (
            <option key={c.chantier_id} value={c.chantier_id}>
              {c.chantier_id} · {c.nom_interne || c.project_title || c.ville || c.adresse_chantier || 'Sans nom'}
              {c.budget_prevu ? ` · ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(c.budget_prevu)}` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Notes internes</h3>
        <Textarea value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Notes, n° BC associé, chantier concerné…" />
      </div>

      {/* Pennylane status */}
      {fa.pennylane_invoice_id && (
        <div key="pennylane-status" className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Pennylane</p>
          <p className="text-sm text-emerald-900 mt-1">
            Synchronisée le {fa.pennylane_pushed_at?.slice(0, 16).replace('T', ' ')} ·
            Facture fournisseur n° <b>{fa.pennylane_invoice_id}</b> ·
            Fournisseur Pennylane #{fa.pennylane_supplier_id}
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}
